<template>
  <v-parallax class="hero" src="/img/neck-massage-square.webp" alt="Head and neck massage therapy">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You are one step away from</div>
        <div class="text-center text-h2 pa-2">lighter neck, clearer mind</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.HeadAndNeckMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our head and neck massage?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.HeadAndNeckMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by our clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text">"{{ review.quote }}"</p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.HeadAndNeckMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Head and Neck Massage?"
    :benefits="benefits"
    :booking-url="$bookingUrls.HeadAndNeckMassage.Default" />

  <section id="pricing">
    <div class="full-width-header">Our Session Options</div>
    <div class="content pb-16">
      <p class="content-intro">
        Choose a quick, focused session designed to ease neck and shoulder tension and leave you feeling light and
        refreshed.
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel the tension lift?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Ease neck stiffness, calm head pressure, and unwind from daily screen strain. Book your head and neck massage
        today.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.HeadAndNeckMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'HeadAndNeckMassageView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Targeted tension release',
          description: 'Focused work on the neck, shoulders, and scalp to ease tightness and headaches.'
        },
        {
          title: 'Posture-friendly relief',
          description: 'Ideal for desk and screen fatigue, helping restore comfort and mobility.'
        },
        {
          title: 'Gentle, precise care',
          description: 'Techniques are tailored to your pressure preferences for a calm, restorative session.'
        }
      ],
      starsImage: '/img/five-stars.png',
      featureReviews: [
        {
          name: 'Pabs Sonico',
          quote:
            'Great neck and back massage. This was my third session and things are getting better. The massage room is very inviting with dim lights and relaxing music in the background.'
        }
      ],
      benefits: [
        {
          title: 'Relieves Tension Headaches',
          description: 'Targeted techniques help ease pressure in the head, reducing the frequency and intensity of tension headaches.'
        },
        {
          title: 'Reduces Neck & Shoulder Stiffness',
          description: 'Loosens tight muscles caused by desk work, screen time, and poor posture, restoring comfortable movement.'
        },
        {
          title: 'Calms the Nervous System',
          description: 'Gentle work around the head and neck activates the body\'s relaxation response, helping to relieve stress and improve focus.'
        }
      ],
      sessionOptions: [
        new BookingOption('15 min', BookingUrls.HeadAndNeckMassage.Min15),
        new BookingOption('20 min', BookingUrls.HeadAndNeckMassage.Min20),
        new BookingOption('25 min', BookingUrls.HeadAndNeckMassage.Min25)
      ]
    };
  }
});
</script>
